import type { ColumnDef, Issue, Person } from '../game/types';
import { IssueCard } from './IssueCard';

interface Props {
  column: ColumnDef;
  issues: Issue[];
  peopleById: ReadonlyMap<string, Person>;
  flagged: ReadonlySet<string>;
  currentSprint: string;
  onToggleFlag: (kind: 'issue' | 'column', id: string) => void;
  onOpenDetail: (issueKey: string) => void;
}

/**
 * A board column.
 *
 * The header is its own flag target, because some defects belong to the column
 * rather than to anything inside it -- a breached WIP limit is the obvious one.
 * A player who spots "too much work in progress" should be able to say so
 * without having to pick an arbitrary card to blame.
 */
export function BoardColumn({
  column,
  issues,
  peopleById,
  flagged,
  currentSprint,
  onToggleFlag,
  onOpenDetail,
}: Props) {
  const columnFlagged = flagged.has(`column:${column.id}`);
  const overLimit = column.wipLimit !== undefined && issues.length > column.wipLimit;

  return (
    <section className="column" id={`col-${column.id}`} aria-label={column.name}>
      <button
        type="button"
        className={`column__header${columnFlagged ? ' is-flagged' : ''}`}
        aria-pressed={columnFlagged}
        aria-label={`Column ${column.name}, ${issues.length} issues${
          column.wipLimit !== undefined ? `, WIP limit ${column.wipLimit}` : ''
        }. ${columnFlagged ? 'Flagged.' : 'Not flagged.'} Activate to toggle.`}
        onClick={() => onToggleFlag('column', column.id)}
      >
        {columnFlagged && (
          <span className="flag-marker" aria-hidden="true">
            ⚑
          </span>
        )}
        <span className="column__name">{column.name}</span>
        <span className="column__count">{issues.length}</span>
        {column.wipLimit !== undefined && (
          <span className={`column__wip${overLimit ? ' column__wip--breached' : ''}`}>
            {issues.length} / {column.wipLimit}
          </span>
        )}
      </button>

      <div className="column__cards">
        {issues.map((issue) => (
          <IssueCard
            key={issue.key}
            issue={issue}
            assignee={
              issue.assigneeId ? peopleById.get(issue.assigneeId) : undefined
            }
            flagged={flagged.has(`issue:${issue.key}`)}
            currentSprint={currentSprint}
            isDoneColumn={column.isDone === true}
            onToggleFlag={() => onToggleFlag('issue', issue.key)}
            onOpenDetail={() => onOpenDetail(issue.key)}
          />
        ))}
      </div>
    </section>
  );
}
